import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle,
  CreditCard, Mail, Globe, Webhook, Tag, RefreshCw, FlaskConical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const GROUP_META = {
  stripe:  { icon: CreditCard, label: 'Stripe (paiements)',  color: 'text-violet-400' },
  email:   { icon: Mail,       label: 'Resend (emails)',     color: 'text-rose-400' },
  webhook: { icon: Webhook,    label: 'Webhook entrant',     color: 'text-cyan-400' },
  app:     { icon: Globe,      label: 'Domaine & app',       color: 'text-emerald-400' },
  pricing: { icon: Tag,        label: 'Tarifs & limites',    color: 'text-amber-400' },
  other:   { icon: Tag,        label: 'Autres',              color: 'text-zinc-400' },
};

export const AdminSettingsPage = () => {
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({}); // { KEY: 'new_value' or '' }
  const [revealed, setRevealed] = useState({}); // { KEY: true } pour afficher la nouvelle valeur tapée
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // 'stripe' | 'resend' | null
  const [testResults, setTestResults] = useState({}); // { stripe: {...}, resend: {...} }

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const setEdit = (key, value) => setEdits(e => ({ ...e, [key]: value }));

  const save = async () => {
    if (Object.keys(edits).length === 0) {
      toast.message('Rien à enregistrer');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { settings: edits }, { withCredentials: true });
      toast.success('Paramètres mis à jour');
      setEdits({});
      setRevealed({});
      fetchSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (kind) => {
    setTesting(kind);
    try {
      const { data } = await axios.post(`${API}/admin/settings/test/${kind}`, {}, { withCredentials: true });
      setTestResults(r => ({ ...r, [kind]: data }));
      if (data.ok) toast.success(`Test ${kind} OK`);
      else toast.error(`Test ${kind} : ${(data.error || '').slice(0, 100)}`);
    } catch (e) {
      const err = e?.response?.data || { error: 'Erreur réseau' };
      setTestResults(r => ({ ...r, [kind]: { ok: false, error: String(err?.detail || err?.error || e.message) } }));
      toast.error('Test échoué');
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  // Group items
  const groups = {};
  items.forEach(it => {
    const g = it.group || 'other';
    if (!groups[g]) groups[g] = [];
    groups[g].push(it);
  });

  const hasUnsaved = Object.keys(edits).length > 0;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-32 space-y-6" data-testid="admin-settings-page">
      {/* Header sticky */}
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-900 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/app" className="text-zinc-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-white tracking-tight truncate">Paramètres système</h1>
            <p className="text-xs text-zinc-500">Configurations modifiables à chaud · accessible uniquement à toi (admin)</p>
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving || !hasUnsaved}
          className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
          data-testid="settings-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer{hasUnsaved && ` (${Object.keys(edits).length})`}
        </Button>
      </div>

      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200">
        <p className="font-semibold flex items-center gap-2 mb-1.5">
          <AlertTriangle className="w-4 h-4" /> Bon à savoir
        </p>
        <ul className="list-disc pl-5 space-y-1 text-xs text-amber-100/80">
          <li>Une valeur <strong>vide</strong> efface l'override et fait revenir à la variable d'environnement par défaut.</li>
          <li>Les clés Stripe et Resend sont <strong>masquées</strong> à l'écran — saisis la nouvelle valeur dans le champ pour la remplacer (laisse vide = inchangé).</li>
          <li>Stripe et Resend sont rechargés <strong>en direct</strong>, sans redéploiement. Pour <code>APP_NAME</code> et <code>PUBLIC_APP_URL</code>, un redémarrage backend peut être nécessaire pour les emails.</li>
        </ul>
      </div>

      {/* Smoke tests rapides */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical className="w-4 h-4 text-amber-400" />
            <h2 className="font-semibold text-white">Tests rapides</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => runTest('stripe')}
              disabled={testing === 'stripe'}
              variant="outline"
              className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
              data-testid="settings-test-stripe"
            >
              {testing === 'stripe' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Tester la connexion Stripe
            </Button>
            <Button
              onClick={() => runTest('resend')}
              disabled={testing === 'resend'}
              variant="outline"
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
              data-testid="settings-test-resend"
            >
              {testing === 'resend' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Envoyer un email de test
            </Button>
          </div>

          {/* Résultats */}
          {Object.entries(testResults).map(([kind, res]) => (
            <div
              key={kind}
              className={`rounded-lg p-3 text-xs font-mono whitespace-pre-wrap ${
                res.ok ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300'
                       : 'bg-red-500/10 border border-red-500/30 text-red-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2 not-italic">
                {res.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                <strong className="font-sans">{kind.toUpperCase()}</strong>
                {res.mode && <span className="ml-auto px-2 py-0.5 rounded bg-zinc-800">{res.mode}</span>}
              </div>
              <pre className="overflow-x-auto">{JSON.stringify(res, null, 2)}</pre>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Groupes de settings */}
      {['stripe', 'email', 'app', 'webhook', 'pricing', 'other'].map(groupKey => {
        const groupItems = groups[groupKey];
        if (!groupItems || groupItems.length === 0) return null;
        const meta = GROUP_META[groupKey] || GROUP_META.other;
        const Icon = meta.icon;
        return (
          <Card key={groupKey} className="bg-zinc-900 border-zinc-800" data-testid={`settings-group-${groupKey}`}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                <Icon className={`w-5 h-5 ${meta.color}`} />
                <h2 className="font-semibold text-white">{meta.label}</h2>
              </div>
              <div className="space-y-4">
                {groupItems.map(it => (
                  <SettingRow
                    key={it.key}
                    item={it}
                    editValue={edits[it.key]}
                    revealed={revealed[it.key]}
                    onChange={(v) => setEdit(it.key, v)}
                    onReveal={() => setRevealed(r => ({ ...r, [it.key]: !r[it.key] }))}
                    onClear={() => setEdit(it.key, '')}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const SettingRow = ({ item, editValue, revealed, onChange, onReveal, onClear }) => {
  const isDirty = editValue !== undefined;
  const sourceBadge = item.source === 'db'
    ? { label: 'BDD (override)', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' }
    : item.source === 'env'
    ? { label: '.env (défaut)',  color: 'bg-zinc-700 text-zinc-300 border-zinc-700' }
    : { label: 'Non configuré',  color: 'bg-red-500/10 text-red-300 border-red-500/30' };

  return (
    <div className="space-y-1.5" data-testid={`setting-${item.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm font-semibold text-zinc-200">{item.label}</label>
            <span className="text-[10px] font-mono text-zinc-600">{item.key}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${sourceBadge.color}`}>
              {sourceBadge.label}
            </span>
          </div>
          {item.hint && <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{item.hint}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <input
            type={item.is_secret && !revealed ? 'password' : 'text'}
            value={editValue !== undefined ? editValue : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={item.is_set ? `Actuel : ${item.value}` : '(vide — saisis la nouvelle valeur)'}
            className={`w-full px-3 py-2 rounded-lg bg-zinc-950 border text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none transition-colors ${
              isDirty ? 'border-amber-500/50 focus:border-amber-500' : 'border-zinc-800 focus:border-zinc-600'
            } ${item.is_secret && !revealed ? 'pr-10' : ''}`}
            data-testid={`setting-input-${item.key}`}
          />
          {item.is_secret && (
            <button
              type="button"
              onClick={onReveal}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-400"
              tabIndex={-1}
            >
              {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
        </div>
        {isDirty && editValue !== '' && (
          <button
            onClick={onClear}
            className="text-xs px-2 py-1 rounded text-zinc-500 hover:text-amber-400"
            title="Annuler la modification"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminSettingsPage;
